import { useEffect } from "react";

import { useLoaderData, useSearchParams } from "@remix-run/react";
import dayjs from "dayjs";

import { SpaceProps } from "~/types";

import { SELLABLE } from "./Layers.client";

import DATA from "~/data"
import { FountainDrawing, HexDrawing, PlanterDrawing, GreenwoodDrawing } from "~/data/assets";

function extractCryptLocations(obj: any): Record<string, any> {
    const result: Record<string, any> = {};

    for (const key in obj) {
        const value = obj[key];
        if (value && typeof value === "object" && "cryptLocations" in value) {
            result[key] = value.cryptLocations;
        }
    }

    return result;
}

export const cryptLocations = extractCryptLocations(DATA.crypts);

export default function FloorPlan({
    selectSpace,
    selectedSpace,
    spaces,
    name,
}: {
    selectSpace: (product: SpaceProps) => void;
    selectedSpace: SpaceProps | null;
    spaces: SpaceProps[];
    name: string;
}) {
    const [searchParams, setSearchParams] = useSearchParams();
    const spaceId = searchParams.get("spaceId");
    const selectedCrypt = searchParams.get("crypt");
    // const { cart } = useLoaderData<typeof loader>();

    const getSelectedCrypt = () => {
        if (spaceId) {
            if (DATA.vendor?.name === "greenwood") {
                const location = cryptLocations[name];
                for (const key in location) {
                    if (
                        location[key].min <= Number(selectedSpace.lotSpaceNbr) &&
                        location[key].max >= Number(selectedSpace.lotSpaceNbr)
                    ) {
                        return key;
                    }
                }
                return null;
            }
            else if (DATA.vendor?.name === "brooklawn") {
                const location = cryptLocations[name];
                for (const key in location) {
                    if (
                        key === selectedSpace?.lotRowNbrAlpha
                    ) {
                        return key;
                    }
                }
            }
            return null;
        }

        return null;
    };

    const setSelectedCrypt = (crypt) => {
        searchParams.set("crypt", crypt);
        setSearchParams(searchParams);
    };

    useEffect(() => {
        if (spaceId) {
            const crypt = getSelectedCrypt();
            setSelectedCrypt(crypt);
        }
    }, [spaceId]);

    return (
        <div className="relative app-container">
            <div className={`transition-wrapper ${selectedCrypt ? "zoom-out" : ""}`}>
                {selectedCrypt === null && (
                    <div className="flex flex-col gap-4 animate-from-bottom-right rotate-in lg:-mt-24 p-8">
                        <h1 className="font-title text-lg md:text-3xl lg:text-5xl text-white md:mb-8 text-center">
                            {name}
                        </h1>
                        <MausoleumOne setSelectedCrypt={setSelectedCrypt} name={name} spaces={spaces} />
                    </div>
                )}
            </div>
            {selectedCrypt !== null && (
                <div className="relative flex flex-col items-center gap-4 animate-from-bottom-right rotate-in lg:-mt-24 p-4 md:p-8">
                    <h1 className="font-title text-lg md:text-3xl lg:text-5xl text-white md:mb-8 text-center">
                        {name} {selectedCrypt}
                    </h1>
                    <Crypt
                        selectSpace={selectSpace}
                        selectedSpace={selectedSpace}
                        selectedCrypt={selectedCrypt}
                        spaces={spaces}
                        name={name}
                    />
                </div>
            )}
        </div>
    );
}

const MausoleumOne = ({
    setSelectedCrypt,
    name,
    spaces,
}: {
    setSelectedCrypt: (crypt: any) => void;
    name: string;
    spaces: SpaceProps[];
}) => {

    if (!Object.keys(DATA.crypts).includes(name)) {
        // EXTRACT - HANDLE COMPONENTS
        return (
            <div className=" text-center max-w-xs mx-auto bg-white p-4 rounded-lg">We apologise, what you are looking for is not available at this time.</div>
        );
    }

    const circles = Object.entries(cryptLocations[name]).map(([title, { cx, cy, min, max }]) => ({
        title,
        cx,
        cy,
        min,
        max,
    }));

    // FIX: static value
    const width = window.innerWidth < 640 ? 90 : 180;
    const height = window.innerWidth < 640 ? 199.5 : 399;



    const DRAWINGS: Record<string, React.ComponentType<React.SVGProps<SVGSVGElement>>> = {
        HEX: HexDrawing,
        PLANTER: PlanterDrawing,
        FOUNTAIN: FountainDrawing,
        West: GreenwoodDrawing,
        East: GreenwoodDrawing,
    }

    const DrawingComponent = DRAWINGS[name];

    return (
        <DrawingComponent>
            {circles.map((circle, index) => {

                const isAvailable = spaces.some(
                    (space) =>
                        space.lotSpaceNbr >= circle.min &&
                        space.lotSpaceNbr <= circle.max &&
                        space.salesStatus === SELLABLE,
                );

                return isAvailable ? (
                    <AvailableCircle
                        key={index}
                        cx={circle.cx}
                        cy={circle.cy}
                        setSelectedCrypt={setSelectedCrypt}
                        title={circle.title}
                        name={name}
                    />
                ) : (
                    <UnavailableCircle key={index} cx={circle.cx} cy={circle.cy} title={circle.title} name={name} />
                );
            })}
        </DrawingComponent>

    );
};

const COLORS = {
    available: "#FFC500",
    unavailable: "#99999952",
    selected: "#3902F6",
    added_to_cart: "#FCE9A5",
};

const Crypt = ({
    cart,
    selectSpace,
    selectedSpace,
    spaces,
    selectedCrypt,
    name,
}: {
    selectSpace: (product: SpaceProps) => void;
    selectedSpace: SpaceProps;
    spaces: SpaceProps[];
    name: string;
}) => {

    const gridWidth = DATA.crypts[name].gridWidth;
    const gridHeight = DATA.crypts[name].gridHeight;

    // FIX: static value
    const squareSize = window.innerWidth < 640 ? 45 : 75;

    const crypt = cryptLocations[name][selectedCrypt];

    const createSquare = (x, y, size, lotSpaceNbr) => {
        // EXTRACT - WE WILL ALWAYS NEED TO UPDATE CODE ?
        const FIND_SPACE = {
            brooklawn: (space: SpaceProps[]) => space.find((space) => Number(space?.lotSpaceNbr) === lotSpaceNbr && space?.section === name && space?.lotRowNbrAlpha === selectedCrypt),
            greenwood: (space: SpaceProps[]) => space.find((space) => Number(space?.lotSpaceNbr) === lotSpaceNbr && space?.lotRowNbrAlpha === name),
        } satisfies Record<string, (space: SpaceProps[]) => SpaceProps | undefined>;

        const getSpace = FIND_SPACE[DATA.vendor?.name as keyof typeof FIND_SPACE];
        const thisSpace = getSpace ? getSpace(spaces) : null;

        if (!thisSpace) {
            return <div className=" text-center max-w-xs mx-auto bg-white p-4 rounded-lg">We apologise, what you are looking for is not available at this time.</div>;
        }

        const isSelected = selectedSpace?.lotSellUnitId === thisSpace?.lotSellUnitId;
        const isSellable = thisSpace?.salesStatus === SELLABLE && !dayjs().isBefore(thisSpace?.lockedUntil);
        const isInCart = !!cart?.spaces.find((cartSpace) => cartSpace.space.lotSellUnitId === thisSpace.lotSellUnitId);

        return (
            <g key={`${x}-${y}`} style={{ cursor: isSellable ? "pointer" : "not-allowed" }}>
                <rect
                    key={`${x}-${y}`}
                    x={x}
                    y={y}
                    width={size}
                    height={size}
                    fill={
                        isSelected
                            ? COLORS.selected
                            : isInCart
                                ? COLORS.added_to_cart
                                : isSellable
                                    ? COLORS.available
                                    : COLORS.unavailable
                    }
                    stroke="white"
                    strokeWidth={2}
                    onClick={() => (isSellable ? selectSpace(thisSpace) : () => { })}
                />
                <text
                    x={x + size / 2}
                    y={y + size / 2}
                    textAnchor="middle"
                    alignmentBaseline="middle"
                    fontSize={size / 3}
                    fill={!isSellable || isSelected ? "white" : "black"}
                    onClick={() => (isSellable ? selectSpace(thisSpace) : () => { })}
                >
                    {lotSpaceNbr}
                </text>
            </g>
        );
    };

    const squares = [];
    for (let row = 0; row < gridHeight; row++) {
        for (let col = 0; col < gridWidth; col++) {
            const x = col * squareSize;
            const y = row * squareSize;
            squares.push(createSquare(x, y, squareSize, crypt.min + squares.length));
        }
    }

    return (
        <section className="relative">
            {DATA.crypts[name].perspectiveInFt && <h2 className="absolute top-[30%] -right-10 text-white text-base">- {DATA.crypts[name].perspectiveInFt}</h2>}
            <svg width={gridWidth * squareSize} height={gridHeight * squareSize}>
                {squares}
            </svg>
        </section>
    );
};

const AvailableCircle = ({
    cx,
    cy,
    name,
    title,
    setSelectedCrypt,
}: {
    cx: number;
    cy: number;
    name: string;
    title: string;
    setSelectedCrypt: (crypt: any) => void;
}) => {
    return (
        <circle
            cx={cx}
            cy={cy}
            r="15"
            fill={COLORS.available}
            onClick={() => setSelectedCrypt(title)}
            style={{ cursor: "pointer" }}
        >
            <title>
                {name} {title}
            </title>
        </circle >
    );
};

const UnavailableCircle = ({ cx, cy, title }: { cx: number; cy: number; title: string }) => {
    return (
        <circle cx={cx} cy={cy} r="15" fill="#D9D9D9">
            <title>{title}</title>
        </circle>
    );
};
